<template>
  <FormKit data-testid="viewer-comments-message-form" type="form" id="messageInputForm" :actions="false" @submit="submit" :config="{ preserveErrors: true }">
    <FormKit
      validation="length:1,200|trim"
      validation-visibility="dirty"
      placeholder="Add a comment..."
      v-model="model"
      type="mention"
      ref="inputRef"
      name="comment"
      id="commentInput"
      data-testid="viewer-comment-input-add"
      inner-class="relative pt-px"
      input-class="pr-16 custom-textarea"
      @input="onInput"
      :tributeOptions="mentionEnabled ? tributeOptions : undefined"
      @keydown.enter="submit"
      @keydown.enter.ctrl.exact="submit"
      @keydown.enter.meta.exact="submit"
    >
      <template #suffix>
        <Button class="textarea-button" @click="submit" type="solid" :variant="model ? 'blue' : 'gray'" icon="general.paperPlane" :disabled="!model"></Button>
      </template>
      <template #prefix>
        <span v-if="!model && mentionEnabled" class="absolute text-sm text-slate-500 right-[55px] top-1/2 -translate-y-1/2">Type @ to mention a person</span>
      </template>
    </FormKit>
    <FormKitMessages :node="inputRef?.node" />
  </FormKit>
</template>
<script lang="ts" setup>
import { FormKit, FormKitMessages } from '@formkit/vue';
import { reset } from '@formkit/core';
import { Ref, nextTick, ref } from 'vue';
import { Button } from '@/storybook/button';
import { useCommentsStore } from '@/modules/viewer/store/useCommentsStore';

withDefaults(defineProps<{ hideLabel?: boolean; mentionEnabled?: boolean }>(), { mentionEnabled: true });

const inputModel = defineModel();
const emit = defineEmits(['submit', 'dirty']);
const commentsStore = useCommentsStore();

const inputRef = ref<any>();
const isDirty = ref(false);
const model: Ref<any> = ref(inputModel.value || '');

const resetForm = () => reset('messageInputForm');

const submit = (e?: KeyboardEvent) => {
  if (e && e.type === 'keydown' && e.key === 'Enter' && e.shiftKey) {
    return;
  }

  if (e && e.preventDefault && e.stopPropagation) {
    e.preventDefault();
    e.stopPropagation();
  }

  inputModel.value = model.value;

  if (!model.value.length) {
    return;
  }

  emit('submit', inputModel.value);
};

const onInput = () => {
  nextTick(() => {
    const fieldDirty = inputModel.value !== model.value;

    if (fieldDirty !== isDirty.value) {
      emit('dirty', fieldDirty);
    }

    isDirty.value = fieldDirty;
  });
};

const tributeOptions = {
  values: async (text: string, cb: Function) => {
    return cb(await commentsStore.searchMentionUsers(text));
  },
  lookup: 'name',
  fillAttr: 'name',
  noMatchTemplate: () => '',
  menuItemLimit: 10,
};

defineExpose({ resetForm });
</script>
<style>
.custom-textarea {
  resize: none;
}
.textarea-button {
  @apply !absolute !right-[5px] !bottom-[5px] !rounded-lg;
}
</style>
